/**
 * The rows of the Tasks table (task management R1), on the list rules the Patients table follows (SHOWCASE-CHANGELOG
 * "Table", "Row icon buttons", "Status"): one 48px DS TableRow per task, status as a glyph with its words, plain secondary
 * text for the source, at most three inline row icon buttons and the rest under the row's ⋯ menu.
 *
 * TaskRow — one task from the store: what needs doing, the patient (name · MRN), source (Manual / Care model), status,
 * due, who holds it. Inline: Resume (on hold, mine), Claim (no one holds it, or reopened), Complete (mine, or a care-model task
 * no one else holds). ⋯: Reassign to a role queue (needs Scrips.Patient #1196 on the environment), in a dialog with the
 * role picker. The server decides every permission; a refusal is said in a toast. A care-model task (origin pathway,
 * PROD-3283) shows its why after the instruction; Complete ticks the engine's item (the run resumes) and completes the
 * task; when it may not be completed here, Complete is disabled and the reason is written after the instruction.
 *
 * CareModelRow — a care-model review step waiting on a person. "Review" opens #774's `ChecklistTask` (the patient
 * Overview's block) in a dialog: ticking resumes the run.
 *
 * Pieces: Signal DS TableRow, TableCell, Modal, Select, Button, AnchoredPopover; the app's GlyphText, RowIconButton,
 * RowActionsTrigger and row MenuItem.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightLeft, CircleCheck, ListChecks, MoreHorizontal, Play, UserCheck } from 'lucide-react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  Button,
  Modal,
  Select,
  TableCell,
  TableRow,
  TEXT_ROLE_CLASSES,
} from '@scripsteam/scrips-signal-ds';
import { ChecklistTask } from '@/patients/components/overview/care-model-tasks-block';
import { MenuItem } from '@/patients/components/patient-row-actions';
import { RowActionsTrigger } from '@/patients/components/row-actions-trigger';
import { usePatientDetail } from '@/encounter/services/use-patient-detail';
import { GlyphText, type GlyphKind } from '@/shared/ui/components/status-glyph';
import { RowIconButton } from '@/shared/ui/components/row-icon-button';
import {
  dueText,
  isCareModelTask,
  isMine,
  isTaskRole,
  ROLE_LABEL,
  STATUS_LABEL,
  TASK_ROLES,
  type ClinicalTask,
  type QueueItem,
  type TaskRole,
} from '@/tasks/lib/clinical-task-wire';
import { useTaskAction, type TaskAction } from '@/tasks/services/use-clinical-tasks';
import type { CareModelReview } from '@/tasks/services/use-care-model-queue';

const patientHref = (patientId: string) => `/patients/edit/${encodeURIComponent(patientId)}`;

export const STEP_UNREAD = 'This care-model step could not be read, so it cannot be completed here. Open the patient record.';

/** Status as a glyph (zone contract C3): open is hollow, claimed half, started full, held amber. */
const STATUS_GLYPH: Record<string, GlyphKind> = {
  requested: 'empty',
  accepted: 'half',
  'in-progress': 'full',
  'on-hold': 'clock',
  completed: 'done',
};

function Meta({ children }: { children: React.ReactNode }) {
  return <span className={TEXT_ROLE_CLASSES.meta}>{children}</span>;
}

function DueCell({ task }: { task: ClinicalTask }) {
  return (
    <span className="inline-flex items-center gap-3">
      {task.priority !== 'routine' ? <GlyphText kind="alert">Urgent</GlyphText> : null}
      {task.isOverdue ? <GlyphText kind="alert">{dueText(task)}</GlyphText> : <Meta>{dueText(task)}</Meta>}
    </span>
  );
}

function ReassignDialog({ onClose, onReassign }: { onClose: () => void; onReassign: (role: TaskRole) => void }) {
  const [role, setRole] = useState<string | undefined>(undefined);
  const footer = (
    <>
      <Button type="button" variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button type="button" disabled={!isTaskRole(role)} onClick={() => isTaskRole(role) && onReassign(role)}>
        Reassign
      </Button>
    </>
  );
  return (
    <Modal open onClose={onClose} title="Reassign task" size="sm" footer={footer}>
      <Select
        label="Reassign to"
        placeholder="Choose a queue"
        value={role}
        options={TASK_ROLES.map((r) => ({ label: ROLE_LABEL[r], value: r as string }))}
        onChange={setRole}
      />
    </Modal>
  );
}

/**
 * `careModelBlock`: for a care-model task, why it may NOT be completed here (#774's `tickBlock` on the step it came from:
 * named to someone else, unreadable, or several items while the engine's resume defect stands), or null when it may.
 */
export function TaskRow({
  item,
  userId,
  careModelBlock = null,
  stepGone = false,
}: {
  item: QueueItem;
  userId: string | null | undefined;
  careModelBlock?: string | null;
  /** The page's care-model read does not list this step: Complete reads the run first (moved on: close; waiting: refuse). */
  stepGone?: boolean;
}) {
  const { task } = item;
  const action = useTaskAction();
  const [menuOpen, setMenuOpen] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const mine = isMine(task, userId);
  // on-hold only moves back to in-progress (Start); it cannot be completed or reassigned from there (store lifecycle).
  const onHold = task.status === 'on-hold';
  const careModel = isCareModelTask(task);
  const queue = isTaskRole(task.performerType) ? ROLE_LABEL[task.performerType] : task.performerType;
  const owner = task.ownerUserId ? (mine ? 'Claimed by you' : 'Claimed by a colleague') : `Waiting in ${queue}`;
  const busy = action.isPending;
  // A refusal is said by the hook, in a toast (it outlives the row).
  const run = (a: TaskAction) => action.mutate(a);

  return (
    <TableRow data-row="task" data-task={task.id} data-mine={mine ? 'true' : undefined}>
      <TableCell data-col="task" className="w-full whitespace-normal">
        {/* Never cut: the why and a block reason are clinical-safety words, so a long one wraps the row. */}
        {task.description}
        {careModel && task.reason ? <Meta>{` · Why: ${task.reason}`}</Meta> : null}
        {careModel && careModelBlock ? <Meta>{` · ${careModelBlock}`}</Meta> : null}
      </TableCell>
      <TableCell data-col="patient" className="whitespace-nowrap">
        <Link to={patientHref(task.patientId)}>
          {item.patientName ?? 'Patient'}
          {item.mrn ? ` · ${item.mrn}` : ''}
        </Link>
      </TableCell>
      <TableCell data-col="source" className="whitespace-nowrap">
        <Meta>
          {careModel ? 'Care model' : 'Manual'}
          {task.kindLabel ? ` · ${task.kindLabel}` : ''}
        </Meta>
      </TableCell>
      <TableCell data-col="status">
        <GlyphText kind={STATUS_GLYPH[task.status] ?? 'empty'}>{STATUS_LABEL[task.status] ?? task.status}</GlyphText>
      </TableCell>
      <TableCell data-col="due" className="whitespace-nowrap">
        <DueCell task={task} />
      </TableCell>
      <TableCell data-col="owner" className="whitespace-nowrap">
        <Meta>{owner}</Meta>
      </TableCell>
      <TableCell data-col="actions">
        <div className="flex items-center justify-end gap-2" data-slot="task-actions">
          {onHold && mine ? (
            <RowIconButton label="Resume" disabled={busy} onClick={() => run({ kind: 'start', task })}>
              <Play className="size-4" aria-hidden="true" />
            </RowIconButton>
          ) : null}
          {/* Claim only what the store lets anyone claim: a task no one holds, or a reopened one (it keeps its owner but
              waits, requested). A task a person holds (accepted, in progress) is never claimable: the store refuses
              accepted → accepted for everyone (ClinicalTaskLifecycle); it changes hands by Reassign. */}
          {!careModel && (!task.ownerUserId || task.status === 'requested') && !onHold ? (
            <RowIconButton label="Claim" disabled={busy} onClick={() => run({ kind: 'claim', task })}>
              <UserCheck className="size-4" aria-hidden="true" />
            </RowIconButton>
          ) : null}
          {!careModel && mine && task.status !== 'requested' && !onHold ? (
            <RowIconButton label="Complete" tone="success" disabled={busy} onClick={() => run({ kind: 'complete', task })}>
              <CircleCheck className="size-4" aria-hidden="true" />
            </RowIconButton>
          ) : null}
          {/* Someone else's claimed care-model task: only they complete it (the engine tick must not run on another's claim). */}
          {careModel && !onHold && (mine || !task.ownerUserId) ? (
            <RowIconButton
              // Disabled, the reason is written in the Task cell (a disabled button shows no tooltip).
              label="Complete"
              tone="success"
              disabled={busy || careModelBlock !== null}
              onClick={() => run({ kind: 'complete-care-model', task, userId, verifyStep: stepGone })}
            >
              <CircleCheck className="size-4" aria-hidden="true" />
            </RowIconButton>
          ) : null}
          {!onHold ? (
            <AnchoredPopover open={menuOpen} onOpenChange={setMenuOpen}>
              <RowActionsTrigger ariaLabel="Task actions" tooltip={`Actions for ${task.description}`}>
                <MoreHorizontal className="size-4" aria-hidden />
              </RowActionsTrigger>
              <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="end" side="bottom" className="w-52 p-1 gap-3">
                <MenuItem
                  icon={ArrowRightLeft}
                  label="Reassign…"
                  onClick={() => {
                    setMenuOpen(false);
                    setReassigning(true);
                  }}
                />
              </AnchoredPopoverContent>
            </AnchoredPopover>
          ) : null}
        </div>
        {reassigning ? (
          <ReassignDialog
            onClose={() => setReassigning(false)}
            onReassign={(performerType) => {
              setReassigning(false);
              run({ kind: 'reassign', task, performerType });
            }}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function patientName(
  d:
    | {
        firstName?: string | null;
        middleName?: string | null;
        lastName?: string | null;
        name?: string | null;
      }
    | undefined,
) {
  if (!d) return null;
  const parts = [d.firstName, d.middleName, d.lastName].filter((x): x is string => !!x && x.trim() !== '');
  return parts.length > 0 ? parts.join(' ') : (d.name ?? null);
}

/** `heldBy`: who the step waits on, as the page knows it (named to you / to a colleague / anyone in the practice). */
export function CareModelRow({ review, heldBy }: { review: CareModelReview; heldBy: string }) {
  const detail = usePatientDetail(review.patientId);
  const name = patientName(detail.data);
  const [open, setOpen] = useState(false);
  const title = review.modelName ? `${review.modelName} review` : 'Care model review';
  return (
    <TableRow data-row="care-model-review" data-enrolment={review.enrolmentId}>
      <TableCell data-col="task" className="w-full whitespace-normal">{title}</TableCell>
      <TableCell data-col="patient" className="whitespace-nowrap">
        <Link to={patientHref(review.patientId)}>{name ?? 'Patient'}</Link>
      </TableCell>
      <TableCell data-col="source" className="whitespace-nowrap">
        <Meta>Care model</Meta>
      </TableCell>
      <TableCell data-col="status">
        <GlyphText kind="empty">Waiting on review</GlyphText>
      </TableCell>
      <TableCell data-col="due" className="whitespace-nowrap">
        <Meta>No due date</Meta>
      </TableCell>
      <TableCell data-col="owner" className="whitespace-nowrap">
        <Meta>{heldBy}</Meta>
      </TableCell>
      <TableCell data-col="actions">
        <div className="flex items-center justify-end gap-2" data-slot="task-actions">
          <RowIconButton label="Review" onClick={() => setOpen(true)}>
            <ListChecks className="size-4" aria-hidden="true" />
          </RowIconButton>
        </div>
        {open ? (
          <Modal open onClose={() => setOpen(false)} title={title} description={name ?? undefined} size="md">
            <ChecklistTask key={`${review.checklist.instanceId}|${review.checklist.activityId}`} step={review.checklist} patientId={review.patientId} />
          </Modal>
        ) : null}
      </TableCell>
    </TableRow>
  );
}
